import { Icon, Menu, useTheme } from '@web-chat-core/ui-kit';
import { Room } from 'livekit-client';
import { ReactNode, useMemo, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useMediaQuery } from 'react-responsive';
import { VirtualBackgroundMenuItem } from 'features/VirtualBackground/components/VirtualBackgroundMenuItem/VirtualBackgroundMenuItem';
import { SoundAndCameraSettingsMenuItem } from 'features/Modals/SoundSettingsModal/components/SoundAndCameraSettingsMenuItem/SoundAndCameraSettingsMenuItem';
import { ObstacleIds } from 'features/MutedSpeechDetection/guardController';
import { isFeatureOn, isWebinarFeatureOn } from 'useCase/featureFlags/featureFlagsApi';
import { FEATURES } from 'consts';
import { RaiseHandMenuItem } from 'features/RaiseHand/components/RaiseHandMenuItem';
import { CallSettingsMenuItem } from 'features/CallSettings/components/CallSettingsMenuItem/CallSettingsMenuItem';
import { useCanSeeCallSettings } from 'features/CallSettings/hooks/useCanSeeCallSettings';
import { WebinarModeMenuItem } from 'features/Webinar';
import { isLocalUserModerator } from 'useCase/moderation/status/moderators';
import { ToggleAdditionalMenuButton } from './view';

interface AdditionalMenuProps {
    room?: Room;
    onMenuOpen?: () => void;
    placement?: 'top' | 'bottom';
}

const contentProps = {
    side: 'top' as const,
    sideOffset: 4,
    // hideWhenDetached: true - скрывает меню, если триггер-компонент в какой-то момент уходит из viewport'а.
    // Но здесь выставлено значение false, поскольку это фиксит редки баг, когда при ресайзе страницы radix может посчитать
    // триггер-компонент обрезанным (хотя он виден) и скрыть меню.
    // В нашем случае hideWhenDetached: false - безопасная настройка, поскольку триггер-компонент не исчезает из viewport'а.
    hideWhenDetached: false,
};

export const AdditionalMenu = observer(({ room, onMenuOpen, placement = 'bottom' }: AdditionalMenuProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery({ query: '(max-width: 991px)' });
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const isVirtualBackgroundsEnabled = isFeatureOn(FEATURES.VCS_VIRTUAL_BACKGROUNDS_ENABLED);
    const isAdvancedSoundAndCameraSettingsEnabled = isFeatureOn(FEATURES.VCS_ADVANCED_SOUND_CAMERA_SETTINGS_ENABLED);
    const isCanSeeCallSettings = useCanSeeCallSettings();
    const isCanSeeWebinarMode = isWebinarFeatureOn() && isLocalUserModerator();

    const menuItems = useMemo(() => {
        const items: ReactNode[] = [];

        if (isMobile && room) {
            items.push(<RaiseHandMenuItem key="RaiseHandMenuItem" participant={room.localParticipant} />);
        }

        if (isVirtualBackgroundsEnabled) {
            items.push(<VirtualBackgroundMenuItem key="VirtualBackgroundMenuItem" />);
        }

        if (isAdvancedSoundAndCameraSettingsEnabled) {
            items.push(<SoundAndCameraSettingsMenuItem key="SoundAndCameraSettingsButton" />);
        }

        if (isCanSeeCallSettings) {
            items.push(<CallSettingsMenuItem key="CallSettingsMenuItem" />);
        }

        if (isCanSeeWebinarMode) {
            items.push(<WebinarModeMenuItem key="WebinarModeMenuItem" />);
        }

        return items;
    }, [
        isMobile,
        room,
        isVirtualBackgroundsEnabled,
        isAdvancedSoundAndCameraSettingsEnabled,
        isCanSeeCallSettings,
        isCanSeeWebinarMode,
    ]);

    const toggleMenu = useCallback(
        (open: boolean) => {
            setIsMenuOpen(open);
            if (open) {
                onMenuOpen?.();
            }
        },
        [onMenuOpen],
    );

    if (menuItems.length === 0) {
        return null;
    }

    return (
        <Menu
            toggleButtonContent={
                <ToggleAdditionalMenuButton>
                    <Icon icon="icDotsSmall" color={theme.root.colors.basicColors['90%']} />
                </ToggleAdditionalMenuButton>
            }
            isForcedOpen={isMenuOpen}
            onToggleMenu={toggleMenu}
            contentProps={placement === 'top' ? contentProps : { side: placement }}
            menuId={ObstacleIds.ControlMoreMenu}
        >
            {menuItems}
        </Menu>
    );
});
